import { objectKeys } from '@iconik-io/core/src/object_entries';
import {
  httpPutJobsState,
  type JobsActionContextSchema,
  type JobsJobElasticSchemaWithId,
} from '@iconik-io/iconik-api/src/services/jobs';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import {
  JOB_COMPLETED_STATUSES_SET,
  JOB_IS_NOT_ABORTABLE_SET,
  JOB_STATUS_FINISHED,
} from '../../constants/jobs';
import { DELETE_JOBS_MODAL } from '../../constants/modals';
import {
  CONTEXT_MENU_ADMIN_STORAGES,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import { historyPush } from '../../ducks/history/actions';
import { openModal } from '../../ducks/modals_manager/slice';
import { ICONS } from '../../icons/icon';
import type { AppDispatch } from '../../store/types';

type ActionContextType = keyof JobsActionContextSchema;

const getDefinition: GetDefinition<JobsJobElasticSchemaWithId> = (props) => {
  const contextItems = props.contextItems;
  const [contextItem] = contextItems;
  const contextItemIds = contextItems.map((job) => job.id);

  const items: ContextMenuItemDefinition[] = [];

  if (
    contextItems.every(
      (item) =>
        !JOB_COMPLETED_STATUSES_SET.has(item.status) &&
        !JOB_IS_NOT_ABORTABLE_SET.has(item.type) &&
        (!item.action_context || item.action_context.ABORT),
    )
  ) {
    items.push({
      label: 'Abort',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          httpPutJobsState({
            job_ids: contextItemIds,
            action: 'ABORT',
          }),
        ),
      icon: ICONS.CrossMark,
      multiple: true,
    });
  }

  if (contextItems.length === 1 && contextItem.has_children) {
    items.push({
      label: 'View subjobs',
      action: () => (dispatch: AppDispatch) =>
        dispatch(historyPush(`/admin/jobs/history/${contextItem.id}/subjobs`)),
      icon: ICONS.File,
    });
  }

  if (
    contextItems.length === 1 &&
    contextItem.object_type === OBJECT_TYPE_ASSETS &&
    contextItem.object_id
  ) {
    items.push({
      label: 'View Asset',
      action: () => (dispatch: AppDispatch) =>
        dispatch(historyPush(`/asset/${contextItem.object_id}`)),
      icon: ICONS.File,
    });
  }

  const actionItemsCount: Record<ActionContextType, number> = {
    PAUSE: 0,
    RESTART: 0,
    ABORT: 0,
    CHANGE_PRIORITY: 0,
    RESUME: 0,
  };
  contextItems.forEach((item) => {
    const actionContext = item.action_context;
    if (!actionContext) {
      return;
    }
    // action_context comes off the API, so only the actions the menu knows
    // about are counted.
    objectKeys(actionItemsCount).forEach((action) => {
      if (action in actionContext) {
        actionItemsCount[action] += 1;
      }
    });
  });

  if (
    actionItemsCount.RESTART === contextItems.length &&
    contextItems.every((job) => job.status !== JOB_STATUS_FINISHED)
  ) {
    items.push({
      label: 'Restart',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          httpPutJobsState({
            action: 'RESTART',
            job_ids: contextItemIds,
          }),
        ),
      icon: ICONS.Refresh,
      multiple: true,
    });
  }

  if (actionItemsCount.PAUSE === contextItems.length) {
    items.push({
      label: 'Pause',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          httpPutJobsState({
            action: 'PAUSE',
            job_ids: contextItemIds,
          }),
        ),
      icon: ICONS.Pause,
      multiple: true,
    });
  }

  if (actionItemsCount.RESUME === contextItems.length) {
    items.push({
      label: 'Resume',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          httpPutJobsState({
            action: 'RESUME',
            job_ids: contextItemIds,
          }),
        ),
      icon: ICONS.Play,
      multiple: true,
    });
  }

  const canDeleteGroup =
    contextItems.filter((job) => JOB_COMPLETED_STATUSES_SET.has(job.status))
      .length === contextItems.length;

  if (canDeleteGroup || props.modifierKeys?.alt) {
    items.push({
      label: 'Delete…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(openModal({ id: DELETE_JOBS_MODAL, contextItems })),
      icon: ICONS.TrashBin,
      multiple: true,
    });
  }

  return {
    items,
    objectType: 'jobs',
  };
};

const AdminJobsContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_ADMIN_STORAGES,
);

export default AdminJobsContextMenu;
